import { PizzaTrackerStep } from 'types/pizzaGraph';
import _ from 'lodash';
import { Stack, SxProps, Theme, Typography } from '@mui/material';
import Chip, { ChipColor } from 'ui-component/extended/Chip';
import { convertDate, formatToShortDate, isBeforeNowDate } from 'utils/dates';
import { isBefore } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import CopyClipboard from 'ui-component/extended/CopyClipboard';
import { useMemo } from 'react';
import UserInfoSection from 'ui-component/UserInfoSection';

interface PizzaTrackerProps {
  steps: PizzaTrackerStep[];
  sx?: SxProps<Theme>;
}

const PizzaTracker = ({ steps, sx }: PizzaTrackerProps) => {
  const theme = useTheme();
  const currentStep = useMemo(
    () => _.chain(steps).filter('completed').last().value()?.key,
    [steps]
  );
  const visibleSteps = useMemo(() => steps.filter((s) => !s.hidden), [steps]);
  const lastIndex = visibleSteps.length - 1;

  const memoizedSteps = useMemo(
    () =>
      visibleSteps.map((step, idx) => {
        const isCurrentStep = currentStep === step.key;

        let color =
          step.color ?? (step.completed ? ChipColor.success : ChipColor.grey);
        if (
          !step.completed &&
          step.estimatedDate &&
          isBeforeNowDate(step.estimatedDate)
        ) {
          color = ChipColor.warning;
        }

        let willMissTarget = false;
        if (!step.completed && step.targetDate && step.estimatedDate) {
          const targetDate = convertDate(step.targetDate);
          const estimatedDate = convertDate(step.estimatedDate);
          if (isBefore(targetDate, estimatedDate)) {
            color = ChipColor.warning;
            willMissTarget = true;
          }
        }

        let willMissPromise = false;
        if (!step.completed && step.promisedDate && step.estimatedDate) {
          const promisedDate = convertDate(step.promisedDate);
          const estimatedDate = convertDate(step.estimatedDate);
          if (isBefore(promisedDate, estimatedDate)) {
            color = ChipColor.warning;
            willMissPromise = true;
          }
        }

        let isPastEstimate = false;
        if (
          !step.completed &&
          step.estimatedDate &&
          isBeforeNowDate(step.estimatedDate)
        ) {
          color = ChipColor.warning;
          isPastEstimate = true;
        }

        const typographyColor =
          _.get(theme.palette, [color, 'main']) ?? theme.palette.grey[500];

        const infoDescription = step.completed
          ? step.infoDescriptionComplete
          : step.infoDescriptionIncomplete;

        return (
          <Chip
            key={step.key}
            label={step.label}
            ChipIcon={step.icon}
            iconColorOverride={typographyColor}
            chipcolor={step.completed ? color : ChipColor.grey}
            sx={{
              border: isCurrentStep ? '1px solid' : '3px solid',
              borderColor: isCurrentStep ? theme.palette.success.main : 'white',
              ...(idx !== lastIndex ? { pr: '20px' } : {}),
              cursor: 'default',
              color: typographyColor,
            }}
            tooltip={
              step.estimatedDate ||
              step.targetDate ||
              step.promisedDate ||
              step.notes ||
              step.actualDate ||
              infoDescription ? (
                <Stack
                  direction="column"
                  spacing={0}
                  sx={{
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                  }}
                >
                  {(willMissPromise || willMissTarget || isPastEstimate) && (
                    <Typography color={typographyColor}>
                      Running Late
                    </Typography>
                  )}
                  {!step.completed && !!step.estimatedDate && (
                    <Typography color={typographyColor}>
                      {step.estimatedLabel || 'Estimated'}:{' '}
                      {formatToShortDate(step.estimatedDate)}
                    </Typography>
                  )}
                  {!!step.completed && !!step.actualDate && (
                    <Typography color={typographyColor}>
                      {formatToShortDate(step.actualDate)}
                    </Typography>
                  )}
                  {willMissTarget && step.targetDate && (
                    <Typography color={typographyColor}>
                      {step.targetLabel || 'Target'}:{' '}
                      {formatToShortDate(step.targetDate)}
                    </Typography>
                  )}
                  {willMissPromise && step.promisedDate && (
                    <Typography color={typographyColor}>
                      {step.promisedLabel || 'Promised'}:{' '}
                      {formatToShortDate(step.promisedDate)}
                    </Typography>
                  )}
                  {!!step.notes && (
                    <Typography variant="caption" color={typographyColor}>
                      {step.notes}
                      <CopyClipboard
                        label={step.copyableNotes ?? step.notes}
                        input={step.copyableNotes ?? step.notes}
                      />
                    </Typography>
                  )}
                  {!!infoDescription && (
                    <Typography
                      variant="caption"
                      color={typographyColor}
                      sx={{ fontStyle: 'italic' }}
                    >
                      {infoDescription}
                    </Typography>
                  )}
                  {!!step.user && <UserInfoSection user={step.user} />}
                </Stack>
              ) : undefined
            }
            tooltipPlacement="bottom"
            tooltipSlotProps={{
              tooltip: {
                sx: {
                  bgcolor: 'white',
                  border: '1px solid',
                  borderColor: theme.palette.grey[300],
                  borderRadius: '8px',
                },
              },
            }}
          />
        );
      }),
    [visibleSteps, currentStep, theme, lastIndex]
  );

  return (
    <Stack
      direction="row"
      spacing="-24px"
      sx={{
        justifyContent: 'flex-start',
        alignItems: 'center',
        ...sx,
      }}
    >
      {memoizedSteps}
    </Stack>
  );
};

export default PizzaTracker;
